import React, { useState, useRef, useEffect } from 'react';
import { getValidNextStatuses, getStatusDisplay } from '../data/funnelStages';

const TERMINAL_KEYS = ['rejected', 'withdrawn'];

function StatusDropdown({ status, onStatusChange, compact }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const display = getStatusDisplay(status);
  const nextStatuses = getValidNextStatuses(status);

  // Split into normal options and terminal options
  const normalOptions = nextStatuses.filter(s => !TERMINAL_KEYS.includes(s));
  const terminalOptions = nextStatuses.filter(s => TERMINAL_KEYS.includes(s));

  const fontSize = compact ? 11 : 13;
  const paddingY = compact ? 2 : 4;
  const paddingX = compact ? 8 : 12;

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Badge */}
      <button
        type="button"
        onClick={() => {
          if (nextStatuses.length > 0) setOpen(prev => !prev);
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize,
          fontWeight: 600,
          lineHeight: 1.4,
          padding: `${paddingY}px ${paddingX}px`,
          borderRadius: 999,
          border: 'none',
          color: display.color,
          backgroundColor: `${display.color}18`,
          cursor: nextStatuses.length > 0 ? 'pointer' : 'default',
          whiteSpace: 'nowrap',
          fontFamily: 'inherit',
          transition: 'filter 0.15s',
        }}
      >
        <span>{display.icon}</span>
        <span>{display.label}</span>
      </button>

      {/* Dropdown */}
      {open && nextStatuses.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            minWidth: 180,
            backgroundColor: '#fff',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {/* Normal (non-terminal) options */}
          {normalOptions.map(key => {
            const d = getStatusDisplay(key);
            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={() => {
                  onStatusChange(key);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onStatusChange(key);
                    setOpen(false);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: d.color,
                  fontWeight: 500,
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={{ width: 18, textAlign: 'center', flexShrink: 0 }}>{d.icon}</span>
                <span>{d.label}</span>
              </div>
            );
          })}

          {/* Divider + terminal options */}
          {terminalOptions.length > 0 && (
            <>
              <div style={{ height: 1, backgroundColor: '#e5e5e5', margin: '4px 0' }} />
              {terminalOptions.map(key => {
                const d = getStatusDisplay(key);
                return (
                  <div
                    key={key}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onStatusChange(key);
                      setOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onStatusChange(key);
                        setOpen(false);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: d.color,
                      fontWeight: 500,
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <span style={{ width: 18, textAlign: 'center', flexShrink: 0 }}>{d.icon}</span>
                    <span>{d.label}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default StatusDropdown;
